// cSpell:ignore Aandeel afrekening Duizendsten gebouw Gebouw Gemeenschappelijk kosten Privaat verdelen Volgt Voorbeeldstraat waarvan Wijzigen
// One cost list (design 2026-10-16, B, labelled as D, in the columns of
// costListColumns.utils). Inside a building every row carries a label in its
// Type column: "Gebouw" for the building's cost, "Privaat" for a unit's own.
// - a unit page lists its own costs and its building's (at the unit's share,
//   changed on the building);
// - a building page lists the building's costs (a legacy group cost's
//   children rolled up into one row) and every unit's own.
// Underneath, the ledgers stay separate: a building's cost is never counted
// on a unit at its whole amount, and no cost is listed twice.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  TGroupCostShareBasis,
  TGroupCostUnitShare,
} from "./groupCostShare.utils";
import { keyDisplayName } from "./keyName.utils";
import { fromCents, roundCents, toCents } from "./money.utils";
import { formatWeight } from "./numberInput.utils";
import { Invoice } from "./types";

export type TCostLevel = "building" | "unit";

// Whether a cost is the building's: booked on the building itself, a common
// cost, or a child of a group cost from before the split moved to the
// settlement (it lives on a unit but is part of a building cost). Every
// other cost is a unit's own.
export const costLevelOf = (params: {
  invoice: Pick<Invoice, "unitId" | "scope" | "groupCostRef">;
  buildingId?: string;
}): TCostLevel => {
  const { invoice, buildingId } = params;
  const isOnBuilding =
    !!buildingId && String(invoice.unitId ?? "") === String(buildingId);
  return isOnBuilding || invoice.scope === "common" || !!invoice.groupCostRef
    ? "building"
    : "unit";
};

// The building page's rows: every cost the page fetched (the building's and
// its units'), each legacy group cost once. The children of one sharing a
// `groupCostRef` become a single building row with their total; its id is
// one of the children's, so opening it lands on a real record (the drawer
// shows the per-unit split).
export const buildingPageCosts = (params: {
  invoices: Invoice[];
  buildingId?: string;
}): Invoice[] => {
  const { invoices, buildingId } = params;
  const byRef = new Map<string, Invoice[]>();
  const rows: Invoice[] = [];
  for (const invoice of invoices) {
    if (invoice.groupCostRef) {
      byRef.set(invoice.groupCostRef, [
        ...(byRef.get(invoice.groupCostRef) ?? []),
        invoice,
      ]);
    } else {
      rows.push(invoice);
    }
  }
  const aggregated = [...byRef.values()].map(children => ({
    ...children[0],
    unitId: undefined,
    groupId: buildingId,
    amountExclVat: roundCents(
      children.reduce((sum, child) => sum + child.amountExclVat, 0),
    ),
  }));
  return [...aggregated, ...rows];
};

// The unit page's rows: its own costs, then its building's costs (each with
// the unit's share, see groupCostShare.utils). Ids are unique: a building's
// costs live on the building, not on the unit. A building's cost that does
// not apply to the unit (see groupCostAppliesToUnit) is left out, so the
// count, the total and the search only see the rows the page shows (Lewis,
// 2026-10-16); the building page keeps every cost.
export const unitPageCosts = (params: {
  ownInvoices: Invoice[];
  buildingInvoices: Invoice[];
  appliesToUnit?: (invoice: Invoice) => boolean;
}): Invoice[] => {
  const { appliesToUnit = () => true } = params;
  const ownIds = new Set(params.ownInvoices.map(invoice => invoice.id));
  return [
    ...params.ownInvoices,
    ...params.buildingInvoices.filter(
      invoice => !ownIds.has(invoice.id) && appliesToUnit(invoice),
    ),
  ];
};

// ----- Totals -----

// What a row adds to a list's total. A row with a share (a building's cost
// on a unit page) adds the unit's share once it is known, and nothing while
// the settlement still has to split it (Q3): the whole amount is the
// building's, not the unit's. A share this viewer cannot redo ("unknown")
// adds nothing either. Any other row adds its own amount.
export const costTotalPart = (params: {
  invoice: Pick<Invoice, "amountExclVat">;
  share?: TGroupCostUnitShare;
}): number => {
  const { invoice, share } = params;
  if (!share) return invoice.amountExclVat;
  return share.status === "known" ? share.amount : 0;
};

export interface ICostListTotals {
  count: number;
  // Excl. VAT, to the cent.
  totalExcl: number;
  // Building costs whose share of this unit the settlement still has to work
  // out ("n kosten nog te verdelen").
  pendingShareCount: number;
  // What the building's costs add to `totalExcl` (on a unit page "waarvan
  // gebouw € 578,00"); 0 without `levelOf`.
  buildingTotalExcl: number;
}

export const costListTotals = (params: {
  invoices: Invoice[];
  shareFor?: (invoice: Invoice) => TGroupCostUnitShare | undefined;
  // Inside a building, to tell the building's costs apart.
  levelOf?: (invoice: Invoice) => TCostLevel | undefined;
}): ICostListTotals => {
  const { invoices, shareFor, levelOf } = params;
  let totalCents = 0;
  let buildingCents = 0;
  let pendingShareCount = 0;
  for (const invoice of invoices) {
    const share = shareFor?.(invoice);
    if (share?.status === "pending") pendingShareCount += 1;
    const partCents = toCents(costTotalPart({ invoice, share }));
    totalCents += partCents;
    if (levelOf?.(invoice) === "building") buildingCents += partCents;
  }
  return {
    count: invoices.length,
    totalExcl: fromCents(totalCents),
    pendingShareCount,
    buildingTotalExcl: fromCents(buildingCents),
  };
};

// ----- The rows -----

// By the cost's date, newest first; the same day by its period, then id, so
// the order never changes between renders.
export const sortCostsNewestFirst = (invoices: Invoice[]): Invoice[] =>
  [...invoices].sort(
    (a, b) =>
      (b.date ?? "").localeCompare(a.date ?? "") ||
      (b.periodFrom ?? "").localeCompare(a.periodFrom ?? "") ||
      String(a.id).localeCompare(String(b.id)),
  );

// What a row of a cost list inside a building says about its level (design
// 2026-10-16, D): a label in its Type column, "Gebouw" for the building's
// cost and "Privaat" for a unit's own. A private cost's unit is named by its
// full address in the label's tooltip (Lewis, 2026-10-16: no line under the
// label any more), on the building page and on the unit page alike.
export interface ICostRowLevel {
  level: TCostLevel;
  label: string;
  // On the building page: the unit as its costs name it ("bus 2"), which
  // the search finds a row by.
  unitLabel?: string;
  // The unit's full address ("Voorbeeldstraat 9 2, 9000 Gent"), for the
  // Privaat label's tooltip; undefined when the unit's address is unknown.
  unitAddress?: string;
}

// A row's level inside a building; none for a unit on its own (it has only
// its own costs) or a list across properties.
export const costRowLevel = (params: {
  invoice: Pick<Invoice, "unitId" | "scope" | "groupCostRef">;
  buildingId?: string;
  page: "group" | "unit";
  // The unit as its costs name it ("bus 2").
  unitLabelOf: (unitId?: string) => string;
  // The unit's full address, as the property's header writes it.
  unitAddressOf?: (unitId?: string) => string | undefined;
}): ICostRowLevel | undefined => {
  const { invoice, buildingId, page, unitLabelOf, unitAddressOf } = params;
  if (!buildingId) return undefined;
  const level = costLevelOf({ invoice, buildingId });
  // "Gemeenschappelijk" (Lewis, 2026-10-17), as the summary's Type column.
  if (level === "building") {
    return { level, label: getLocalizedText("costs.label.common") };
  }
  return {
    level,
    label: getLocalizedText("costs.label.private"),
    unitLabel: page === "group" ? unitLabelOf(invoice.unitId) : undefined,
    unitAddress: unitAddressOf?.(invoice.unitId),
  };
};

// ----- Who changes a row -----

export interface ICostRowAccess {
  // Changed (and deleted) right here.
  isEditable: boolean;
  // Read here, changed on the building: "Wijzigen op het gebouw".
  editOnBuilding: boolean;
}

// Who may change a row of a cost list. A unit page changes its own costs;
// its building's costs are only read there and changed on the building, for
// a user who may change the building's. A building page changes its own
// costs, and a unit's own costs when the user may change that unit's (Q7),
// never those of an archived unit. Nothing changes on an archived page.
export const costRowAccess = (params: {
  page: "group" | "unit";
  level: TCostLevel;
  // The row is a cost of the unit's building, listed on the unit page.
  isBuildingCostOnUnit: boolean;
  isPageArchived: boolean;
  // The user may change the page's own property's costs.
  canEditPage: boolean;
  // On a unit page: the user may change its building's costs.
  canEditBuilding?: boolean;
  // On a building page: the user may change the row's unit's costs, and
  // whether that unit is archived.
  canEditUnit?: boolean;
  isUnitArchived?: boolean;
}): ICostRowAccess => {
  const { page, level, isBuildingCostOnUnit, isPageArchived } = params;
  if (page === "unit" && isBuildingCostOnUnit) {
    return { isEditable: false, editOnBuilding: !!params.canEditBuilding };
  }
  if (isPageArchived) return { isEditable: false, editOnBuilding: false };
  if (page === "unit" || level === "building") {
    return { isEditable: params.canEditPage, editOnBuilding: false };
  }
  return {
    isEditable: !!params.canEditUnit && !params.isUnitArchived,
    editOnBuilding: false,
  };
};

// ----- The share in the amount column -----

// How a unit's share of a building cost was found: the key and the unit's
// weight in it (written like the statement, "2 / 6,5"), or the method.
export const shareBasisLabel = (
  basis?: TGroupCostShareBasis,
): string | undefined => {
  if (!basis) return undefined;
  switch (basis.kind) {
    case "key":
      return `${
        basis.keyName
          ? keyDisplayName(basis.keyName)
          : getLocalizedText("costs.distribution_method.key")
      } ${formatWeight(basis.weight)} / ${formatWeight(basis.totalWeight)}`;
    case "consumption":
      return basis.consumptionUnit
        ? getLocalizedText("costs.distribution_method.consumption_with_unit", {
            unit: basis.consumptionUnit === "kwh" ? "kWh" : "m³",
          })
        : getLocalizedText("costs.distribution_method.consumption");
    case "company":
      return getLocalizedText("costs.distribution_method.company");
    case "amount":
      return getLocalizedText("costs.group_share.basis_amount");
  }
};

// What the amount column shows for a building's cost on a unit page: only
// the unit's share ("€ 378,00"); while the settlement still has to split it,
// "Volgt in de afrekening van het gebouw". How the share was found goes in
// the amount's tooltip, one sentence (Lewis, 2026-10-16: the lines under the
// amount confused): "Aandeel van bus 2: € 378,00 van € 1.260,00
// (Duizendsten 300 / 1.000)". The cost's drawer keeps the full detail.
export const shareCellTexts = (params: {
  share: TGroupCostUnitShare;
  amountExclVat: number;
  format: (amount: number) => string;
  // The unit as its building names it ("bus 2").
  unitLabel?: string;
}): { amount: string; subLines: string[]; tooltip: string } => {
  const { share, amountExclVat, format } = params;
  const unit =
    params.unitLabel?.trim() || getLocalizedText("costs.group_share.this_unit");
  const total = format(amountExclVat);
  if (share.status === "pending") {
    return {
      amount: getLocalizedText("costs.group_share.pending"),
      subLines: [],
      tooltip: getLocalizedText("costs.group_share.tooltip_pending", {
        unit,
        total,
      }),
    };
  }
  if (share.status !== "known") {
    return {
      amount: "—",
      subLines: [],
      tooltip: getLocalizedText("costs.group_share.of_total", {
        amount: total,
      }),
    };
  }
  const basis = shareBasisLabel(share.basis);
  const sentence = getLocalizedText("costs.group_share.tooltip", {
    unit,
    share: format(share.amount),
    total,
  });
  return {
    amount: format(share.amount),
    subLines: [],
    tooltip: basis ? `${sentence} (${basis})` : sentence,
  };
};
